import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';
import { get } from 'lodash';

import { useEventHub } from '@satellite/composables/useEventHub';
import assetVisitService from '@satellite/services/asset-visit-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, Nullable } from '@nova/core';

export interface YardAsset {
  id?: Maybe<string>;
  code?: Nullable<string>;
  type?: Nullable<string>;
  assetVisit?: {
    visitType?: Nullable<string>;
    spotAssignments?: Array<{
      spotId?: Maybe<string>;
    }>;
  };
  spotAssignments?: Array<{
    spotId?: Maybe<string>;
  }>;
}

export interface UseYardAssetPresenterOptions {
  assetId?: Ref<Maybe<string>>;
  /**
   * Initial asset data (already fetched). Used only for initial state.
   * If live=true, this will be refetched and overridden.
   */
  asset?: Ref<Maybe<YardAsset>> | ComputedRef<Maybe<YardAsset>> | Maybe<YardAsset>;
  /**
   * If true, fetches immediately when component mounts or when assetId changes.
   * If false, defers fetching until component enters viewport (requires v-intersect).
   * Default: true
   */
  immediate?: boolean;
  /**
   * If true, the asset will be refreshed automatically when the asset is updated (via eventHub).
   */
  live?: boolean;
}

export function useYardAssetPresenter(options: UseYardAssetPresenterOptions = {}) {
  const { assetId, asset, immediate = true, live = false } = options;
  const eventHub = useEventHub();

  const fetchedAsset = ref<Nullable<YardAsset>>(null);
  const isLoading = ref(false);
  const needsFetch = ref(false);
  // When immediate=true, we don't need viewport tracking - always consider it "in viewport"
  // When immediate=false, start as false and let v-intersect update it
  const isInViewport = ref(immediate);

  const effectiveAssetId = computed((): Maybe<string> => {
    const explicitAssetId = unref(assetId);

    if (explicitAssetId) {
      const initialAssetId = toValue(asset)?.id;

      if (initialAssetId && initialAssetId !== explicitAssetId) {
        console.warn(
          '[useYardAssetPresenter] assetId mismatch:',
          `explicit assetId (${explicitAssetId}) does not match asset.id (${initialAssetId}). Using explicit assetId.`
        );
      }

      return explicitAssetId;
    }

    return toValue(asset)?.id || null;
  });

  const currentAsset = computed(() => {
    return {
      ...toValue(asset),
      ...fetchedAsset.value
    };
  });

  const assetCode = computed((): Nullable<string> => {
    if (!currentAsset.value) {
      return null;
    }

    return currentAsset.value.code || null;
  });

  const assetType = computed((): Nullable<string> => {
    if (!currentAsset.value) {
      return null;
    }

    return currentAsset.value.type || null;
  });

  const visitType = computed((): Nullable<string> => {
    if (!currentAsset.value) {
      return null;
    }

    return get(currentAsset.value, 'assetVisit.visitType') || null;
  });

  const spotId = computed((): Maybe<string> => {
    if (!currentAsset.value) {
      return null;
    }

    return (
      get(currentAsset.value, 'spotAssignments[0].spotId') ??
      get(currentAsset.value, 'assetVisit.spotAssignments[0].spotId') ??
      null
    );
  });

  const fetchAsset = async (id: string): Promise<void> => {
    if (!id) {
      fetchedAsset.value = null;
      return;
    }

    isLoading.value = true;
    needsFetch.value = false;

    try {
      const response = await assetVisitService.getAssetContainerById(id);
      fetchedAsset.value = response;
    } catch (err) {
      console.error('[useYardAssetPresenter] Failed to fetch asset:', err);

      const error = err instanceof Error ? err : new Error(String(err));

      fetchedAsset.value = null;

      // throw error for callers that await
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = async (onError?: (error: Error) => void): Promise<void> => {
    const id = effectiveAssetId.value;
    if (!id) {
      fetchedAsset.value = null;
      needsFetch.value = false;
      return;
    }

    // Only check viewport when immediate=false (used in components with v-intersect)
    // When immediate=true, fetch immediately regardless of viewport
    if (!immediate && !isInViewport.value) {
      needsFetch.value = true;
      return;
    }

    try {
      needsFetch.value = false;
      await fetchAsset(id);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useYardAssetPresenter] Failed to refresh asset:', error);

      if (onError) {
        onError(error);
      }

      // throw error for callers that await
      throw error;
    }
  };

  const handleIntersect = async (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;

    isInViewport.value = isIntersecting;

    // If component is in viewport and we have a pending fetch or live=true, refresh to ensure fresh data
    // This handles cases where events arrived while component was not visible
    if (isIntersecting && (needsFetch.value || live)) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  const handleUpdateAssetContainerEvent = async (event: { id: Nullable<string> }) => {
    if (event?.id === effectiveAssetId.value) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  watch(
    () => unref(assetId),
    async newId => {
      if (newId) {
        fetchedAsset.value = null;
        // Fire-and-forget for automatic initial fetch
        try {
          await refresh();
        } catch {
          // Error already logged in refresh()
        }
      } else {
        fetchedAsset.value = null;
        needsFetch.value = false;
      }
    }
  );

  onMounted(async () => {
    const hasCode = Boolean(assetCode.value);
    const hasType = Boolean(assetType.value);

    if (immediate || !hasCode || !hasType) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }

    if (live) {
      eventHub.on('update-AssetContainer', handleUpdateAssetContainerEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-AssetContainer', handleUpdateAssetContainerEvent);
    }
  });

  return {
    asset: currentAsset,
    assetCode,
    assetType,
    spotId,
    visitType,
    isLoading,
    refresh,
    onIntersect: handleIntersect,
    needsFetch
  };
}
